import { Redirect } from "@shopify/app-bridge/actions"
import { Result } from "arg"
import { observer } from "mobx-react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { ActionData, APPEnum, CreateSubscribeData, PBAPI, PBPage, PBPageAction, PeriodEnum } from "react-pp-bundle"

import accountApi from "@/api/account"
import { AppPricingInterval, PlanPricing } from "@/api/account/type.d"
import { SubscriptionPaymentMethodEnum } from "@/models/IUserInfo"
import DowngradeModal, { type TempPlanInfo } from "@/pages/Account/components/DowngradeModal"
import stores from "@/stores"
import AppBridge from "@/utils/appBridge"
import toast from "@/utils/toast"

const BundlePage = () => {
  const { t } = useTranslation(["common", "account"])
  const [btnLoading, setBtnLoading] = useState(false)
  const [showDowngradeModal, setShowDowngradeModal] = useState(false)
  const [tempPlanInfo, setTempPlanInfo] = useState<TempPlanInfo | null>(null)
  const [pendingActionData, setPendingActionData] = useState<ActionData | null>(null)
  const { userInfoSore, upgradeStore } = stores
  const { currentPlan } = upgradeStore
  const isAPPToBundle = !userInfoSore.data.subscription_payment_method

  // 转换PeriodEnum到AppPricingInterval的辅助函数
  function convertPeriodToInterval(period: any): AppPricingInterval {
    // 根据PeriodEnum枚举转换为AppPricingInterval
    if (period === PeriodEnum.Year) {
      return AppPricingInterval.Annual
    } else if (period === PeriodEnum.Month) {
      return AppPricingInterval.Every30_Days
    }
    // 默认返回月费
    return AppPricingInterval.Every30_Days
  }

  function checkIsShowDowngradeModal(data: ActionData, force = false): boolean {
    const selectedPlan = data.pbData.currentPriceInfo["1"]
    // 当前套餐信息
    const currentPlanIsMonthly = currentPlan.interval === AppPricingInterval.Every30_Days
    const currentPlanIsYearly = currentPlan.interval === AppPricingInterval.Annual
    const currentPlanLevel = currentPlan.level || 0

    // 即将订阅的套餐信息
    const selectedPlanLevel = selectedPlan.kind
    // 将selectedPlan.period转换为统一的AppPricingInterval枚举
    const selectedPlanInterval = convertPeriodToInterval(selectedPlan.period)
    const selectedPlanIsMonthly = selectedPlanInterval === AppPricingInterval.Every30_Days
    const selectedPlanIsYearly = selectedPlanInterval === AppPricingInterval.Annual

    // 判断是否为降级操作
    const isPlanDowngrade = selectedPlanLevel < currentPlanLevel

    if (isPlanDowngrade) {
      console.log("套餐档位降级")
    }

    /**
     * 判断是否降级，各种升级降级场景判断
     */

    // 升套餐版本的降级（低版本年费 → 高版本月费）
    const isLowYearToHighMonth = currentPlanIsYearly
        && selectedPlanIsMonthly
        && selectedPlanLevel > currentPlanLevel

    // 降套餐版本的升级（高版本月费 → 低版本年费）
    const isHighMonthToLowerYear = currentPlanIsMonthly
      && selectedPlanIsYearly
      && selectedPlanLevel < currentPlanLevel

    // 高版本年费切月费的降级（高版本年费 → 同等级或更低等级月费）
    const isHighYearToMonth = currentPlanIsYearly
      && selectedPlanIsMonthly
      && selectedPlanLevel <= currentPlanLevel

    // 标准降级（同计费周期下的套餐等级降级）
    const isStandardDowngrade = selectedPlan.app_plan_id < currentPlan.pricing_id
      && ((currentPlanIsMonthly && selectedPlanIsMonthly) || (currentPlanIsYearly && selectedPlanIsYearly))

    // 综合判断是否为降级操作
    // 1. 通过comparison字段判断 (-1表示降级，0/1表示升级)
    // 2. 或者符合各种降级场景
    const planNameList = {
      2: "Essential",
      3: "Professional",
      4: "Enterprise",
    }

    const selectedPlanName = planNameList[selectedPlanLevel] || `Level ${selectedPlanLevel} Plan`

    const isOverallDowngrade = selectedPlanLevel < currentPlanLevel
      || isLowYearToHighMonth && !isAPPToBundle
      || isHighMonthToLowerYear
      || isHighYearToMonth
      || isStandardDowngrade && !isAPPToBundle

    console.log("降级判断调试信息:", {
      // 当前套餐信息
      currentPlanInterval: currentPlan.interval,
      currentPlanPriceId: currentPlan.pricing_id,
      currentPlanLevel: currentPlanLevel,
      currentPlanName: currentPlan.plan_name,
      currentPlanIsYearly,
      // 选择的套餐信息
      selectedPlanPeriod: selectedPlan.period,
      selectedPlanInterval,
      selectedPlanLevel,
      selectedPlanName,
      selectedPlanIsYearly,
      selectedPlanId: selectedPlan.id,
      // 枚举对比
      PeriodEnumMonth: PeriodEnum.Month,
      PeriodEnumYear: PeriodEnum.Year,
      // 降级场景判断
      isLowYearToHighMonth,
      isHighMonthToLowerYear,
      isHighYearToMonth,
      isStandardDowngrade,
      isOverallDowngrade,
    })

    // app套餐切换bundle套餐，套餐等级相同的情况下，不弹窗
    const sameLevel = isAPPToBundle && selectedPlan.kind === currentPlan.level

    if (sameLevel){
      return false
    }
    /* 如果是降级操作且未强制执行，显示确认弹窗 */
    if (!force && isOverallDowngrade) {
      setShowDowngradeModal(true)
      setPendingActionData(data) // 存储完整的action数据
      // 临时存储刚刚选中的降级套餐信息
      setTempPlanInfo({
        level: selectedPlanLevel,
        planName: selectedPlanName,
        activePrice: selectedPlan as unknown as PlanPricing,
        isLowYearToHighMonth,
        isHighMonthToLowerYear,
        isHighYearToMonth,
        // 当前套餐信息
        currentPlanInterval: upgradeStore.bundlePayType === 1 ? AppPricingInterval.Every30_Days : AppPricingInterval.Annual,
        currentPlanPriceId: currentPlan.pricing_id,
        currentPlanLevel: currentPlanLevel,
        isAPPToBundle,
      })
      return true
    }

    // 其他情况判断
    // 处理普通套餐切换到bundle套餐的情况（视为升级）

    // 返回是否触发了降级确认流程
    return false
  }

  // 检查取消订阅是否需要降级确认
  function checkCancelSubscriptionDowngrade(): boolean {
    // 如果当前已经是免费套餐或Level 0，不需要确认
    if (currentPlan.level <= 1) {
      return false
    }

    // 创建模拟的免费套餐数据用于降级确认
    const freePlanData = {
      level: 0,
      planName: "Free",
      activePrice: {
        id: 0,
        comparison: -1, // 表示降级
      } as unknown as PlanPricing,
      isLowYearToHighMonth: false,
      isHighMonthToLowerYear: false,
      isHighYearToMonth: false,
      // 当前套餐信息
      currentPlanInterval: currentPlan.interval,
      currentPlanPriceId: currentPlan.pricing_id,
      currentPlanLevel: currentPlan.level || 0,
    }

    setShowDowngradeModal(true)
    setTempPlanInfo(freePlanData)

    return true
  }

  // 处理订阅结果
  const executeSubscribeResult = (res: Result<CreateSubscribeData>) => {
    if (res.code === 1000 && res.data) {
      const { confirmation_url } = res.data

      if (AppBridge.exist()) {
        const redirect = Redirect.create(AppBridge.app)

        redirect.dispatch(Redirect.Action.REMOTE, confirmation_url)
      } else {
        // 否则用 Window 对象调用打开页面
        window.open(confirmation_url)
      }
    } else {
      toast(t("common:ServerError"), 4e3, true)
    }
  }
  // 更新订阅

  async function updatePlan(plans: ActionData["plans"]) {
    setBtnLoading(true)
    try {
      const res = await PBAPI.createSubscribe({ plans })

      executeSubscribeResult(res)
      closeBtnLoading()
    } catch {
      handleSubscribeRequestError()
    }
  }

  // 外部包请求不经过 baseRequest 拦截器：失败时自行提示并立即复位 loading
  const handleSubscribeRequestError = () => {
    toast(t("common:ServerError"), 4e3, true)
    window.PB_PAGE_LOADING(false)
    setBtnLoading(false)
  }

  // 确认降级操作
  const handleDowngradeConfirm = async () => {
    if (!tempPlanInfo) return

    setShowDowngradeModal(false)

    // 判断是否为取消订阅操作
    if (tempPlanInfo.level === 0) {
      // 执行取消订阅
      await executeCancelPlan()
    } else if (pendingActionData) {
      // 执行套餐更新
      await updatePlan(pendingActionData.plans)
      setPendingActionData(null)
    }

    setTempPlanInfo(null)
  }

  // 取消降级操作
  const handleDowngradeCancel = () => {
    setShowDowngradeModal(false)
    setTempPlanInfo(null)
    setPendingActionData(null)
  }

  // 五秒后关闭loading
  const closeBtnLoading = () => {
    setTimeout(() => {
      setBtnLoading(false)
    }, 5000)
  }
  // 执行取消订阅

  async function executeCancelPlan() {
    setBtnLoading(true)
    // 询问产品是否需要弹窗
    window.PB_PAGE_LOADING(true)
    let res: Awaited<ReturnType<typeof PBAPI.cancelSubscribe>>

    try {
      res = await PBAPI.cancelSubscribe()
    } catch {
      handleSubscribeRequestError()
      return
    }

    if (res.code === 1000) {
      setTimeout(() => {
        // 更新bundle数据
        upgradeStore.getBundleData()
        userInfoSore.updateUserInfo()
        accountApi.getCurrentSubscription()
          .then((res) => {
            const { current_discount } = userInfoSore.data

            upgradeStore.setCurrentPlan(res.data.current_subscription, current_discount ? current_discount.interval : undefined)
          })
      }, 2000)

      toast(t("account:DowngradedSuccessfully"), 4e3)
    } else {
      toast(t("common:ServerError"), 4e3, true)
    }
    window.PB_PAGE_LOADING(false)
    setBtnLoading(false)
  }

  useEffect(() => {
    return () => {
      // stores.upgradeStore.setPreferKind(undefined)
      // stores.upgradeStore.setPerferPlanId(undefined)
    }
  }, [])

  return (
    <>
      <PBPage
        app={APPEnum.Tracking}
        loading={upgradeStore.bundleLoading}
        data={upgradeStore.bundleData}
        setData={(v) => {
          upgradeStore.setBundleData(v)
        }}
        // 推荐购买的套餐Plan ID 也叫 Price id
        perferPlanId={upgradeStore.perferPlanId}
        // 希望购买的套餐，Plan id 套餐ID
        perferKind={upgradeStore.preferKind}
        period={upgradeStore.bundlePayType}
        setPeriod={upgradeStore.setBundlePayType}
        // 公司bundle套餐订阅，禁用
        btnDisabled={userInfoSore.data.subscription_payment_method === SubscriptionPaymentMethodEnum.CHANNELWILL}
        btnLoading={btnLoading}
        onAction={async function (action: PBPageAction, data: ActionData) {
          const { plans } = data

          // 判断升级or降级
          const actionMap = {
            async choosePlan() {
              // 如果是公司bundle套餐订阅，弹窗拦截
              if (userInfoSore.data.subscription_payment_method === SubscriptionPaymentMethodEnum.CHANNELWILL) {
                upgradeStore.setShowBundleChangePlanReminder(true)
                return
              }

              // 检查是否需要降级确认
              if (checkIsShowDowngradeModal(data)) {
                // 降级弹窗已通过state管理显示
                return
              }

              // 非降级情况，直接执行订阅
              await updatePlan(plans)
            },

            // 点击取消订阅
            async cancelPlan() {
              if (checkCancelSubscriptionDowngrade()) {
                // 降级弹窗已通过state管理显示
                return
              }
              // 直接执行取消订阅
              await executeCancelPlan()
            },

            async resumePlan () {
              window.PB_PAGE_LOADING(true)
              setBtnLoading(true)
              try {
                const res = await PBAPI.createSubscribe({ plans: data.resumePlan })

                executeSubscribeResult(res)
                window.PB_PAGE_LOADING(false)
                closeBtnLoading()
              } catch {
                handleSubscribeRequestError()
              }
            },
          }

          await actionMap[action]?.()
        }}
      />

      {/* 降级提醒Modal */}
      {tempPlanInfo && showDowngradeModal && (
        <DowngradeModal
          show={showDowngradeModal}
          onClose={handleDowngradeCancel}
          confirm={handleDowngradeConfirm}
          currentSubscription={upgradeStore.currentPlan}
          tempPlanInfo={tempPlanInfo}
          isBundle
          isAPPToBundle={!userInfoSore.data.subscription_payment_method}
          currentPlan={upgradeStore.plans.find(plan => plan.level === upgradeStore.currentPlan.level)}
        />
      )}
    </>
  )
}

export default observer(BundlePage)
